import React, { useEffect, useRef, useState } from "react";
import { Loader } from "google-maps";
import { config } from "../core";
import { apiInspections } from "../api/inspections";
import { apiInspectors } from "../api/inspectors";
import { getTextColorForBackground } from "../utilities/colours";

const COMPLETED_STATUSES = ["completed", "submitted"];
const ROUTE_COLOR = "#2563eb"; // not started
const STARTED_COLOR = "orange"; // in progress
const COMPLETED_COLOR = "#00acdc"; // completed/submitted (matches Planner)
const MAP_ID = "8041c9b566fb264"; // project's configured map ID (same as Tracking page)

// Module-level cache so the Google Maps JS + marker library load once per page,
// not per map. preloadRouteMap() kicks the load off early (when the routes-today
// section opens) so the first row expand is fast — like the Tracking page which
// loads on mount.
let googlePromise = null;
let markerPromise = null;
export function loadGoogle() {
  if (!googlePromise) {
    googlePromise = new Loader(config.mapsKeyPlanner, { version: "weekly", libraries: ["places", "geometry"] }).load();
  }
  return googlePromise;
}
async function loadMarkerLibrary() {
  const google = await loadGoogle();
  if (!markerPromise) { markerPromise = google.maps.importLibrary("marker"); }
  return markerPromise;
}
export function preloadRouteMap() {
  loadGoogle().then(() => loadMarkerLibrary()).catch(() => {});
}

// Colour by status, matching the Planner page's convention.
function colorFor(ins) {
  if (COMPLETED_STATUSES.includes(ins.Status)) { return COMPLETED_COLOR; }
  if (ins.StartedAt && !ins.StartedAt.startsWith("0001")) { return STARTED_COLOR; }
  return ROUTE_COLOR;
}

// Truck SVG (white) used by the Tracking page for the inspector's live location.
const TRUCK_SVG = `<svg style="fill:white;width:28px;height:28px;display:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path d="M171.3 80L224 80l0 16 0 80 0 16L99.4 192l34.7-86.9C140.3 90 155 80 171.3 80zM272 192l0-16 0-80 0-16 81.2 0c12.2 0 23.6 5.5 31.2 15l77.6 97L272 192zm252 .6L422 65c-16.7-20.9-42-33-68.7-33L171.3 32c-36 0-68.3 21.9-81.7 55.3l-42.8 107C19.8 201.8 0 226.6 0 256L0 368c0 17.7 14.3 32 32 32l33.3 0c7.6 45.4 47.1 80 94.7 80s87.1-34.6 94.7-80l130.7 0c7.6 45.4 47.1 80 94.7 80s87.1-34.6 94.7-80l33.3 0c17.7 0 32-14.3 32-32l0-48c0-66.7-50.9-121.4-116-127.4zM434.7 368a48 48 0 1 1 90.5 32 48 48 0 1 1 -90.5-32zm135.8-16c-13.2-37.3-48.7-64-90.5-64s-77.4 26.7-90.5 64l-138.9 0c-13.2-37.3-48.7-64-90.5-64s-77.4 26.7-90.5 64L48 352l0-96c0-8.8 7.2-16 16-16l448 0c44.2 0 80 35.8 80 80l0 32-21.5 0zM160 336a48 48 0 1 1 0 96 48 48 0 1 1 0-96z"/></svg>`;

// InspectorRouteMap renders a single inspector's route for a date: numbered
// markers for each route inspection (faded when completed) + their live
// location (truck icon), fit to bounds. Just the map — no progress card /
// inspection list / auto-refresh (unlike the Tracking page).
export default function InspectorRouteMap({ inspectorId, date }) {
  const mapEl = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let map = null;
    let infoWindow = null;
    let markers = [];
    let polyline = null;
    let googleRef = null;

    (async () => {
      try {
        const google = await loadGoogle();
        googleRef = google;
        if (cancelled || !mapEl.current) { return; }
        map = new google.maps.Map(mapEl.current, {
          center: { lat: 27.5, lng: -82.5 },
          zoom: 7,
          mapId: MAP_ID,
        });
        infoWindow = new google.maps.InfoWindow();
        const { AdvancedMarkerElement } = await loadMarkerLibrary();
        const bounds = new google.maps.LatLngBounds();
        let hasPoints = false;

        const [routeResp, locResp] = await Promise.all([
          apiInspections.getRoute(inspectorId, date).catch(() => null),
          apiInspectors.getLocation(inspectorId).catch(() => null),
        ]);
        if (cancelled) { return; }

        const routeInspections = routeResp?.data?.result?.RouteInspections || [];
        routeInspections.forEach((ins, idx) => {
          if (!ins.Lat || !ins.Lng) { return; }
          const tag = document.createElement("div");
          tag.textContent = idx + 1;
          const color = colorFor(ins);
          tag.style.backgroundColor = color;
          tag.style.color = getTextColorForBackground(color);
          tag.style.padding = "6px 8px";
          tag.style.fontSize = "0.8rem";
          tag.style.borderRadius = "4px";
          tag.style.fontWeight = "bold";
          const marker = new AdvancedMarkerElement({ map, position: { lat: ins.Lat, lng: ins.Lng }, content: tag });
          markers.push(marker);
          marker.addListener("click", () => {
            infoWindow.setContent(
              `<div><a href="/inspections/${ins.PolicyNo}/process/pre_r" target="_blank" style="font-weight:bold">${ins.PolicyNo}</a>` +
              `${ins.Address ? `<br>${ins.Address}` : ""}</div>`
            );
            infoWindow.open({ anchor: marker, map });
          });
          bounds.extend({ lat: ins.Lat, lng: ins.Lng });
          hasPoints = true;
        });

        const loc = locResp?.data?.result;
        if (loc && loc.ID > 0 && loc.Lat && loc.Long) {
          const tag = document.createElement("div");
          tag.style.backgroundColor = ROUTE_COLOR;
          tag.style.borderRadius = "50%";
          tag.style.padding = "10px";
          tag.style.boxShadow = "0 0 0 3px white, 0 0 12px 4px rgba(0,0,0,0.4)";
          tag.innerHTML = TRUCK_SVG;
          const marker = new AdvancedMarkerElement({ map, position: { lat: loc.Lat, lng: loc.Long }, content: tag });
          markers.push(marker);
          bounds.extend({ lat: loc.Lat, lng: loc.Long });
          hasPoints = true;
        }

        // actual driving route line (VROOM whole-route geometry, else Google
        // per-leg polylines, else straight lines). Gated behind arcpro-route-polygons.
        if (config.routePolygonsEnabled) {
          const rd = routeResp?.data?.result;
          let routePath = [];
          if (rd?.RoutePolyline) {
            routePath = google.maps.geometry.encoding.decodePath(rd.RoutePolyline);
          } else if (rd?.RouteInspections) {
            for (const s of rd.RouteInspections) {
              if (s.Polylines) { routePath = routePath.concat(google.maps.geometry.encoding.decodePath(s.Polylines)); }
              else if (s.Lat || s.Lng) { routePath.push({ lat: s.Lat, lng: s.Lng }); }
            }
          }
          if (routePath.length > 1) {
            polyline = new google.maps.Polyline({ path: routePath, map, strokeColor: "#1d4ed8", strokeOpacity: 0.75, strokeWeight: 7 });
          }
        }

        if (hasPoints) { map.fitBounds(bounds); }
        if (!cancelled) { setLoading(false); }
      } catch (e) {
        if (!cancelled) { setError("Error loading map"); setLoading(false); }
      }
    })();

    return () => {
      cancelled = true;
      markers.forEach((m) => {
        if (googleRef) { googleRef.maps.event.clearInstanceListeners(m); }
        m.map = null;
      });
      if (polyline) { polyline.setMap(null); }
      if (infoWindow) { infoWindow.close(); }
      if (map && googleRef) { googleRef.maps.event.clearInstanceListeners(map); }
    };
  }, [inspectorId, date]);

  return (
    <div className="tw-relative">
      {loading && <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-text-sm">Loading map…</div>}
      <div ref={mapEl} style={{ height: 340, width: "100%" }} className="tw-rounded-lg tw-shadow-sm tw-bg-gray-100" />
      {error && <div className="tw-text-red-500 tw-text-sm tw-mt-1">{error}</div>}
    </div>
  );
}